import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack
} from '@mui/material';
import { createUser } from '../services/api';

const UserManagement = () => {
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', roles: ['supplier'], domain: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleAddUser = async () => {
    try {
      // Extract domain from email if not provided
      const domain = newUser.domain ? newUser.domain : newUser.email.split('@')[1];
      
      await createUser(newUser.email, newUser.roles, domain);
      setAddUserOpen(false);
      setNewUser({ email: '', roles: ['supplier'], domain: '' });
      showSnackbar('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to create user', 'error');
    }
  };
  
  const handleRoleChange = (event) => {
    setNewUser({
      ...newUser,
      roles: event.target.value
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2">
            User Management
          </Typography>
          <Button 
            variant="contained"
            color="primary"
            onClick={() => setAddUserOpen(true)}
          >
            Add New User
          </Button>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Create user accounts that can access the platform. Users are authenticated via Auth0 
          but must also be registered here to access the system.
        </Typography>
      </Paper>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              fullWidth
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              required
            />
            
            <FormControl fullWidth>
              <InputLabel id="user-roles-label">Roles</InputLabel>
              <Select
                labelId="user-roles-label"
                multiple
                value={newUser.roles}
                onChange={handleRoleChange}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Stack>
                )}
              >
                <MenuItem value="supplier">Supplier</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Domain (optional)"
              fullWidth
              value={newUser.domain}
              onChange={(e) => setNewUser({ ...newUser, domain: e.target.value })}
              helperText="If left empty, domain will be extracted from email"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained" color="primary">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement; 